import { useEffect, useState } from "react";
import { getMe, linkAnonymous, type UserRow } from "./lib/api";
import { initAuth, handleCallback, subscribe, getCurrentUser } from "./lib/auth";
import { SYSTEM_PROMPT, TEACHER_NAME } from "./lib/persona";
import { Onboarding } from "./components/Onboarding";
import { Dashboard, type LessonStartArgs } from "./components/Dashboard";
import { Lesson } from "./components/Lesson";
import { AuthButton } from "./components/AuthButton";
import { Landing } from "./components/landing/Landing";
import { AdminDashboard } from "./components/AdminDashboard";

type View = "loading" | "callback" | "landing" | "onboarding" | "dashboard" | "lesson" | "admin";

async function refreshUser(): Promise<{ user: UserRow | null; isAdmin: boolean }> {
  try {
    const { user, isAdmin } = await getMe();
    return { user, isAdmin: Boolean(isAdmin) };
  } catch {
    return { user: null, isAdmin: false };
  }
}

function pickView(
  user: UserRow | null,
  isAdmin: boolean,
  oidcAuthenticated: boolean,
): View {
  if (!user || user.is_anonymous) {
    // If OIDC says we're signed in but backend hasn't paired us yet
    // (race / cleared anon cookie / token-not-sent), don't drop to Landing.
    return oidcAuthenticated ? "onboarding" : "landing";
  }
  if (isAdmin) return "admin";
  if (user.level === null || user.level === undefined) return "onboarding";
  return "dashboard";
}

export function App(): JSX.Element {
  const [view, setView] = useState<View>("loading");
  const [user, setUser] = useState<UserRow | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lessonArgs, setLessonArgs] = useState<LessonStartArgs | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const isCallback = window.location.pathname === "/auth/callback";
        if (isCallback) {
          setView("callback");
        }

        await initAuth();

        if (isCallback) {
          try {
            await handleCallback();
            await linkAnonymous().catch(() => null);
            const back = sessionStorage.getItem("post_login");
            sessionStorage.removeItem("post_login");
            if (back && back.startsWith("/")) {
              window.location.replace(back);
              return;
            }
            window.history.replaceState({}, "", "/");
          } catch (err) {
            setError(`Sign-in failed: ${err instanceof Error ? err.message : String(err)}`);
          }
        }

        let { user: u, isAdmin: admin } = await refreshUser();
        // If OIDC says signed-in but backend returned anonymous, the link
        // didn't take (race condition or cookie cleared) — retry once.
        if (getCurrentUser() && (!u || u.is_anonymous)) {
          try {
            await linkAnonymous();
          } catch {
            /* noop */
          }
          ({ user: u, isAdmin: admin } = await refreshUser());
        }
        setUser(u);
        setIsAdmin(admin);
        setView(pickView(u, admin, !!getCurrentUser()));
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setView("landing");
      }
    })();

    const off = subscribe(async () => {
      const { user: u, isAdmin: admin } = await refreshUser();
      setUser(u);
      setIsAdmin(admin);
      setView(pickView(u, admin, !!getCurrentUser()));
    });
    return off;
  }, []);

  if (view === "loading" || view === "callback") {
    return (
      <div className="h-full flex items-center justify-center bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-ink-600 border-t-lime-400 rounded-full animate-spin" />
          <div className="text-ink-400 text-xs uppercase tracking-widest">
            {view === "callback" ? "Signing you in…" : "Loading…"}
          </div>
          {error && (
            <div className="bg-red-900/40 border border-red-700 text-red-200 rounded-xl px-4 py-2 text-xs mt-3 max-w-md">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (view === "landing") {
    return <Landing />;
  }

  if (view === "admin" && user) {
    return <AdminDashboard user={user} />;
  }

  if (view === "onboarding") {
    return (
      <div className="relative h-full">
        <div className="absolute top-3 right-3 z-50">
          <AuthButton variant="header" />
        </div>
        <Onboarding
          teacherName={TEACHER_NAME}
          onDone={(u) => {
            setUser(u);
            setView(pickView(u, isAdmin, !!getCurrentUser()));
          }}
        />
      </div>
    );
  }

  if (view === "dashboard" && user) {
    return (
      <div className="h-full flex flex-col bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900">
        <div className="flex items-center justify-end px-6 pt-3">
          <AuthButton variant="header" />
        </div>
        <div className="flex-1 overflow-hidden">
          <Dashboard
            user={user}
            teacherName={TEACHER_NAME}
            onStart={(args) => {
              setLessonArgs(args);
              setView("lesson");
            }}
          />
        </div>
      </div>
    );
  }

  if (view === "lesson" && user) {
    return (
      <Lesson
        user={user}
        teacherName={TEACHER_NAME}
        systemPromptBase={SYSTEM_PROMPT}
        mode={lessonArgs?.mode ?? "free"}
        unitPromptBlock={lessonArgs?.promptBlock}
        unitSlug={lessonArgs?.unit?.slug}
        onExit={() => {
          setLessonArgs(null);
          setView("dashboard");
        }}
      />
    );
  }

  return (
    <div className="h-full flex items-center justify-center bg-ink-900 text-ink-300">
      Unexpected state.
    </div>
  );
}
